/*
 * Copyright (c) Nexign, JSC, 2021-2022.
 */
/**
 * Campaign Page routing
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @version 1.21.0
 * @since 1.12.0
 */

import { useCallback } from 'react';

import { Redirect, Route, RouteComponentProps, Switch } from 'react-router';

import { EBreadCrumbType } from '@modules/breadCrumbs/breadCrumbsTypes';
import { AccessComponentKeyEnum } from '@modules/role/constants/guard/guardConstants';
import CorporatePage from '@pages/CorporatePage/CorporatePage';
import { PageNotFound } from '@pages/ErrorPage/PageNotFound';
import { ECampaignsType } from '@pages/PageCampaign';
import PageCampaign from '@pages/PageCampaign/Loadable';
import PageCampaignInfo from '@pages/PageCampaignInfo/Loadable';

import { IRouteParams, RoutePath } from '../constants/routesConstants';
import PrivateRoute from '../PrivateRoute';

const CampaignsRoute = () => {

    const renderRedirectAccountComponent = useCallback((routeProps: RouteComponentProps<IRouteParams>) => (
        <Redirect to={{
            pathname: RoutePath.campaignsAccount,
            state: {
                filter: { type: ECampaignsType.account, entityId: routeProps.match.params.clientId },
            },
        }} />
    ), []);

    const renderRedirectSubscriberComponent = useCallback((routeProps: RouteComponentProps<IRouteParams>) => (
        <Redirect to={{
            pathname: RoutePath.campaignsSubscriber,
            state: {
                filter: { type: ECampaignsType.subscriber, entityId: routeProps.match.params.subscriberId },
            },
        }} />
    ), []);

    return (
        <Switch>
            <PrivateRoute
                exact
                accessKey={AccessComponentKeyEnum.PageCampaign}
                component={PageCampaign}
                path={[RoutePath.campaignsAccount, RoutePath.campaignsSubscriber]}
                wrapper={CorporatePage}
            />

            <Route
                exact
                component={renderRedirectAccountComponent}
                path={RoutePath.campaignsByClient}
            />

            <Route
                exact
                component={renderRedirectSubscriberComponent}
                path={RoutePath.campaignsByMsisdn}
            />
            <Route exact path={RoutePath.campaigns}>
                <Redirect to={RoutePath.campaignsAccount}/>
            </Route>

            <PrivateRoute
                accessKey={AccessComponentKeyEnum.PageCampaignInfo}
                breadCrumbs={[EBreadCrumbType.campaigns]}
                component={PageCampaignInfo}
                path={RoutePath.campaignInfo}
                wrapper={CorporatePage}
            />

            <PrivateRoute
                accessKey={AccessComponentKeyEnum.PageNotFound}
                component={PageNotFound}
                path="**"
            />
        </Switch>
    );
};

export default CampaignsRoute;
